import {useQuery, useQueryClient} from '@tanstack/react-query';
import type {QueryClient} from '@tanstack/react-query';
import React from 'react';

import {api} from 'app/modules/Remote';
import type {ApiImageryCheckoutPreview} from 'app/modules/Remote/api';
import type {ApiImageryPrepaid} from 'app/modules/Remote/Organization';

/** The organization's prepaid credit. Checkouts draw on it, so it's refetched each time a page mounts. */
export function imageryPrepaidsQuery(organizationId: string) {
  return {
    queryKey: ['imageryPrepaids', organizationId],
    queryFn: () => api.organizations.imageryPrepaids(organizationId),
    staleTime: 0,
  };
}

export const prepaidRemainingCents = (prepaids: ApiImageryPrepaid[]): number =>
  prepaids.reduce((total, prepaid) => total + prepaid.remainingCents, 0);

/**
 * The balance a checkout is judged against. A balance that won't load means paying by card, so it
 * isn't retried: the buyer would wait out the backoff only to be asked for a card anyway.
 *
 * Previews are priced against the balance as it stood, so they're refetched when what's left
 * changes, and only then: each one is a call to Stripe.
 */
export function useCheckoutPrepaids(organizationId: string, enabled: boolean) {
  const queryClient = useQueryClient();
  const query = useQuery({...imageryPrepaidsQuery(organizationId), enabled, retry: false});
  const remainingCents = query.data && prepaidRemainingCents(query.data);
  const lastRemainingCents = React.useRef(remainingCents);
  React.useEffect(() => {
    if (remainingCents === undefined) {
      return;
    }
    if (lastRemainingCents.current !== undefined && lastRemainingCents.current !== remainingCents) {
      void queryClient.invalidateQueries({queryKey: ['checkoutPreview']}, {cancelRefetch: false});
    }
    lastRemainingCents.current = remainingCents;
  }, [remainingCents, queryClient]);
  return query;
}

/** Credit pays for the whole charge, so it needs no card. */
export const isCoveredByCredit = (preview: ApiImageryCheckoutPreview): boolean =>
  preview.totalCents === 0 && (preview.prepaidCents ?? 0) > 0;

/** For after a checkout answer that spent credit, or showed what's on screen was out of date. */
export const invalidateCheckoutCredit = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({queryKey: ['checkoutPreview']}),
    queryClient.invalidateQueries({queryKey: ['imageryPrepaids']}),
  ]);
